import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowLeft,
  faBolt,
  faClockRotateLeft,
  faComments,
  faPaperPlane,
  faPlus,
  faRobot,
  faRotateRight,
  faTrash,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { ChatbotMessageComponent, SlotChipToggleEvent } from '../message/chatbot-message.component';
import { TooltipDirective } from '../../tooltip/tooltip.directive';
import type {
  ChatMessage,
  ChatNavigation,
  ChatSession,
  ChatSlotFillSelectMode,
  ChatSuggestion,
  ChatUsage,
} from '../chatbot.types';

export interface ChatSlotChipToggle {
  messageId: string;
  fieldKey: string;
  value: string;
  mode: ChatSlotFillSelectMode;
}

const DEFAULT_WIDTH = 420;

@Component({
  selector: 'ams-chatbot',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, FontAwesomeModule, ChatbotMessageComponent, TooltipDirective],
  styleUrl: './chatbot.component.scss',
  templateUrl: './chatbot.component.html',
  host: {
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class ChatbotComponent implements AfterViewChecked, AfterViewInit {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  readonly faPaperPlane = faPaperPlane;
  readonly faRobot = faRobot;
  readonly faBolt = faBolt;
  readonly faComments = faComments;
  readonly faPlus = faPlus;
  readonly faClockRotateLeft = faClockRotateLeft;
  readonly faArrowLeft = faArrowLeft;
  readonly faXmark = faXmark;
  readonly faTrash = faTrash;
  readonly faRotateRight = faRotateRight;
  readonly faTriangleExclamation = faTriangleExclamation;

  readonly open = input(false);
  readonly title = input('Membora AI');
  readonly contextLabel = input<string | null>(null);
  readonly emptyTitle = input('How can I help you today?');
  readonly emptySubtitle = input('Ask me anything about members, events, or association data.');
  readonly emptySuggestions = input<ChatSuggestion[]>([]);
  readonly placeholder = input('Ask Membora AI…');
  readonly disclaimer = input('AI can make mistakes. Verify important information.');
  readonly error = input<string | null>(null);
  readonly showCitations = input(false);
  readonly isLoading = input(false);
  readonly messages = input<ChatMessage[]>([]);
  readonly history = input<ChatSession[]>([]);
  // When history().length >= historyLimit, the history pane shows an amber
  // banner warning the user that starting a new chat will replace the oldest
  // one. Driven by the same constant as the backend prune-at-cap policy.
  readonly historyLimit = input<number>(50);
  // Per-message slot-fill chip selections, keyed by messageId then fieldKey.
  // The host (chatbot-panel) owns this state and reflects it back so chip
  // toggle styles re-render when state changes.
  readonly slotSelections = input<Record<string, Record<string, string[]>>>({});
  // AI credit + chat-session usage shown in the footer meter. Null hides the
  // meter entirely (e.g. before the first fetch, or on fetch failure).
  readonly usage = input<ChatUsage | null>(null);
  // When set, the composer (textarea + send) is disabled and this reason is
  // shown in place of the disclaimer — used to block input up front when the
  // tenant is out of AI credits, instead of letting a send bounce off a 403.
  readonly sendDisabledReason = input<string | null>(null);

  readonly closed = output<void>();
  readonly messageSubmitted = output<string>();
  readonly suggestionSelected = output<ChatSuggestion>();
  readonly navigationSelected = output<ChatNavigation>();
  readonly actionConfirmed = output<string>();
  readonly actionDismissed = output<string>();
  readonly sessionSelected = output<ChatSession>();
  readonly sessionDeleted = output<ChatSession>();
  readonly newChatRequested = output<void>();
  readonly slotChipToggled = output<ChatSlotChipToggle>();
  // Emitted when the user clicks the Retry button on the error banner. Host
  // panel re-submits the last user message; see chatbot-panel for the
  // last-request tracking.
  readonly retryRequested = output<void>();

  readonly isHistoryFull = computed(() => this.history().length >= this.historyLimit());

  // Credit fill % for the meter bar. Null when unlimited (platform) or cap is
  // unknown — the template then shows a count without a bar.
  readonly creditPercent = computed<number | null>(() => {
    const u = this.usage();
    if (!u || u.creditsCap === null || u.creditsCap <= 0) return null;
    return Math.min(100, Math.round((u.creditsUsed / u.creditsCap) * 100));
  });

  readonly showHistory = signal(false);
  readonly displayMessages = computed<ChatMessage[]>(() => {
    const messages = this.messages();
    if (!this.isLoading()) {
      return messages;
    }

    return [
      ...messages,
      {
        id: 'typing',
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isTyping: true,
      },
    ];
  });

  draft = '';
  inputFocused = false;

  private shouldScrollToBottom = false;
  private readonly savedWidth = signal<number | null>(null);
  private readonly dragWidth = signal<number | null>(null);
  // Drives the --resizing class that suppresses the width transition mid-drag
  // (the transition makes per-mousemove width changes look jerky).
  protected readonly isResizing = signal(false);
  private isDragging = false;
  private dragStartX = 0;
  private dragStartWidth = 0;
  private boundMouseMove: ((event: MouseEvent) => void) | null = null;
  private boundMouseUp: (() => void) | null = null;
  private boundWindowBlur: (() => void) | null = null;

  readonly effectiveWidth = computed<number | null>(() => {
    if (!this.open()) {
      return null;
    }

    return this.dragWidth() ?? this.savedWidth();
  });

  readonly messagesEnd = viewChild<ElementRef<HTMLElement>>('messagesEnd');
  readonly messagesContainer = viewChild<ElementRef<HTMLElement>>('messagesContainer');
  readonly inputEl = viewChild<ElementRef<HTMLTextAreaElement>>('inputEl');

  constructor() {
    this.destroyRef.onDestroy(() => this.cleanupResizeListeners());

    effect(() => {
      if (!this.open() || this.showHistory()) {
        return;
      }

      this.displayMessages().length;
      this.shouldScrollToBottom = true;
    });
  }

  onEscape(): void {
    if (this.open()) {
      this.close();
    }
  }

  ngAfterViewInit(): void {
    this.autoResize();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollMessagesToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  autoResize(): void {
    const inputElement = this.inputEl()?.nativeElement;
    if (!inputElement) {
      return;
    }

    if (inputElement.value.length === 0) {
      inputElement.style.height = '';
      return;
    }

    inputElement.style.height = 'auto';
    inputElement.style.height = `${inputElement.scrollHeight}px`;
  }

  close(): void {
    this.closed.emit();
  }

  onResizeHandleMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.isResizing.set(true);
    this.dragStartX = event.clientX;
    this.dragStartWidth = this.effectiveWidth() ?? DEFAULT_WIDTH;

    this.boundMouseMove = currentEvent => this.onResizeMouseMove(currentEvent);
    this.boundMouseUp = () => this.onResizeMouseUp();
    this.boundWindowBlur = () => this.onResizeMouseUp();

    this.document.addEventListener('mousemove', this.boundMouseMove);
    this.document.addEventListener('mouseup', this.boundMouseUp);
    window.addEventListener('blur', this.boundWindowBlur);
    this.document.body.style.userSelect = 'none';
    this.document.body.style.cursor = 'ew-resize';
  }

  private onResizeMouseMove(event: MouseEvent): void {
    if (!this.isDragging) {
      return;
    }

    const delta = this.dragStartX - event.clientX;
    const minWidth = 300;
    const maxWidth = window.innerWidth * 0.7;
    this.dragWidth.set(Math.min(Math.max(this.dragStartWidth + delta, minWidth), maxWidth));
  }

  private onResizeMouseUp(): void {
    const finalWidth = this.dragWidth();
    this.isDragging = false;
    this.isResizing.set(false);
    this.cleanupResizeListeners();

    if (finalWidth !== null) {
      this.savedWidth.set(finalWidth);
      this.dragWidth.set(null);
    }
  }

  private cleanupResizeListeners(): void {
    if (this.boundMouseMove) {
      this.document.removeEventListener('mousemove', this.boundMouseMove);
      this.boundMouseMove = null;
    }

    if (this.boundMouseUp) {
      this.document.removeEventListener('mouseup', this.boundMouseUp);
      this.boundMouseUp = null;
    }

    if (this.boundWindowBlur) {
      window.removeEventListener('blur', this.boundWindowBlur);
      this.boundWindowBlur = null;
    }

    this.document.body.style.userSelect = '';
    this.document.body.style.cursor = '';
  }

  newChat(): void {
    this.newChatRequested.emit();
    this.showHistory.set(false);
  }

  toggleHistory(): void {
    this.showHistory.update(value => !value);
  }

  loadSession(session: ChatSession): void {
    this.sessionSelected.emit(session);
    this.showHistory.set(false);
    this.shouldScrollToBottom = true;
  }

  sessionPreview(session: ChatSession): string {
    // Backend-derived title (saved on first turn) wins. Fallback derives a
    // label from the first user message — only relevant when the title is
    // missing (e.g. legacy sessions persisted before titles were wired).
    if (session.title && session.title.trim().length > 0) {
      return session.title;
    }
    const firstUserMessage = session.messages.find(message => message.role === 'user');
    if (!firstUserMessage) {
      return 'Empty session';
    }

    return firstUserMessage.content.length > 60
      ? `${firstUserMessage.content.slice(0, 60)}…`
      : firstUserMessage.content;
  }

  requestDeleteSession(session: ChatSession, event: Event): void {
    // Stop the click from also firing loadSession on the parent row.
    event.stopPropagation();
    this.sessionDeleted.emit(session);
  }

  emitRetry(): void {
    this.retryRequested.emit();
  }

  sendSuggestion(suggestion: ChatSuggestion): void {
    this.suggestionSelected.emit(suggestion);
  }

  emitSlotChipToggle(messageId: string, event: SlotChipToggleEvent): void {
    this.slotChipToggled.emit({ messageId, ...event });
  }

  slotSelectionsForMessage(messageId: string): Record<string, string[]> {
    return this.slotSelections()[messageId] ?? {};
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage(): void {
    const content = this.draft.trim();
    if (!content || this.isLoading() || this.sendDisabledReason()) {
      return;
    }

    this.draft = '';
    setTimeout(() => this.autoResize(), 0);
    this.messageSubmitted.emit(content);
    this.shouldScrollToBottom = true;
  }

  private scrollMessagesToBottom(): void {
    const container = this.messagesContainer()?.nativeElement;
    const end = this.messagesEnd()?.nativeElement;
    if (!container && !end) {
      return;
    }

    const applyScroll = () => {
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      end?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    applyScroll();
    requestAnimationFrame(() => {
      applyScroll();
      requestAnimationFrame(applyScroll);
    });
    setTimeout(applyScroll, 0);
  }
}
